import { FC, useState } from 'react';
import { InnerTooltipWrapper, QcsTooltip } from '../common/basic/QcsTooltip';
import { useTranslation } from 'react-i18next';
import { QcsButton } from '../common/basic/QcsButton';
import { FaIcon } from '../common/FaIcon';
import { faUserLock } from '@fortawesome/free-solid-svg-icons';
import { getEmployeeId, InvitedPersonsPinModal } from '../invitation/Detail/Tabs/InvitedPersonsPinModal';
import { MenuItem, useTheme } from '@mui/material';
import { QcsLoadingButton } from '../common/basic/QcsLoadingButton';
import { QcsListItemIcon } from '../common/basic/QcsListItemIcon';
import { QcsListItemText } from '../common/basic/QcsListItemText';
import { SupplierEmployeeDto } from '@api/models/suppliers/SupplierEmployeeDto';
import { InvitedPersonDto } from '@api/models/invitation/InvitedPersonDto';
import { UserRole } from '@api/models/enums/UserRole';
import { useAppSelector } from '../../store';
import { selectIdentity } from '../../store/entities/identity';
import { hasRole } from '../../utils/roles';
import { Identity } from '../../models/auth';

export const canRevealPin = (identity: Identity, person: InvitedPersonDto | SupplierEmployeeDto): boolean => {
  if (hasRole(identity.roles, [UserRole.ExternalManager])) {
    // Persons of a one-time invitation carry no supplier; those the API still lets a manager read.
    return !person.supplier?.id || person.supplier.id === identity.supplier;
  }

  if (hasRole(identity.roles, [UserRole.ExternalWorker, UserRole.ExternalOneTime])) {
    return getEmployeeId(person) === identity.id;
  }

  return true;
};

export interface Props {
  pinDisabled?: boolean;
  person: InvitedPersonDto | SupplierEmployeeDto;
  buttonType: 'icon' | 'btn' | 'menuItem';
  textKey?: string;
}

export const ShowPinBtn: FC<Props> = ({ pinDisabled, person, buttonType, textKey }) => {
  const { t } = useTranslation();
  const labelKey = textKey ?? 'invitation.tabs.tooltips.pinEmployee';
  const [invitedPersonForPin, setInvitedPersonForPin] = useState<InvitedPersonDto | SupplierEmployeeDto>();
  const theme = useTheme();
  const identity = useAppSelector(selectIdentity);

  const handlePinClose = () => {
    setInvitedPersonForPin(undefined);
  };

  const handleShowPin = () => {
    setInvitedPersonForPin(person);
  };

  if (!canRevealPin(identity, person)) {
    return null;
  }

  return (
    <>
      <InvitedPersonsPinModal invitedPerson={invitedPersonForPin} onClose={handlePinClose} />
      {buttonType !== 'menuItem' && (
        <QcsTooltip title={t(labelKey)} placement="top">
          <InnerTooltipWrapper>
            {buttonType === 'icon' && (
              <QcsButton stopPropagation onClick={handleShowPin} disabled={pinDisabled}>
                <FaIcon icon={faUserLock} color={pinDisabled ? undefined : theme.palette.success.main} />
              </QcsButton>
            )}
            {buttonType === 'btn' && <QcsLoadingButton onClick={handleShowPin}>{t('supplierEmployee.revealPin')}</QcsLoadingButton>}
          </InnerTooltipWrapper>
        </QcsTooltip>
      )}
      {buttonType === 'menuItem' && (
        <MenuItem onClick={handleShowPin} disabled={pinDisabled}>
          <QcsListItemIcon>
            <FaIcon icon={faUserLock} color={pinDisabled ? undefined : theme.palette.common.black} />
          </QcsListItemIcon>
          <QcsListItemText>{t(labelKey)}</QcsListItemText>
        </MenuItem>
      )}
    </>
  );
};
